import React, { useEffect, useMemo, useRef, useState } from 'react';
import { reportClick, reportImpression } from '../../services/promo';
import './PromoSlot.scss';

/**
 * 広告1枠の描画。
 *
 * promo は get_creative の戻り値そのまま。null なら何も描画しない。
 * display_type が 'slideshow' のときは assets を順に切り替える。
 * 各画像の duration_ms があればそれを、無ければ slide_interval_ms を使う。
 *
 * クラス名に "ad" を使わないこと。EasyList の `##.ad-slot` は全ドメイン対象なので、
 * 広告ブロッカーを入れたブラウザでは localhost でも要素ごと消える。
 */
const PromoSlot = ({ promo, placementSlug, keyword, variant = 'table', style, onPromoClick }) => {
  const [index, setIndex] = useState(0);
  const [opening, setOpening] = useState(false);
  // 広告が差し替わったら1枚目に戻す
  const promoIdRef = useRef(null);

  // 毎レンダーで新しい配列を作ると useEffect が回り続けるので memo 化する
  const assets = useMemo(() => promo?.assets ?? [], [promo]);
  const isSlideshow = promo?.display_type === 'slideshow' && assets.length > 1;

  if (promo && promoIdRef.current !== promo.creative_id) {
    promoIdRef.current = promo.creative_id;
    if (index !== 0) setIndex(0);
  }

  // 表示ログは「実際に画面へ出た時点」で1回だけ。
  // 同じクリエイティブが出続けている間は重複して記録しない
  // （StrictMode の二重実行でも増えないよう、記録済みキーを ref で持つ）
  const loggedRef = useRef(null);
  useEffect(() => {
    if (!promo || !placementSlug) return;
    const key = `${promo.creative_id}:${placementSlug}`;
    if (loggedRef.current === key) return;
    loggedRef.current = key;
    reportImpression(promo.creative_id, placementSlug, keyword);
  }, [promo, placementSlug, keyword]);

  useEffect(() => {
    if (!isSlideshow) return undefined;
    const wait = assets[index]?.duration_ms ?? promo?.slide_interval_ms ?? 4000;
    const timer = setTimeout(
      () => setIndex((i) => (i + 1) % assets.length),
      Math.max(300, wait),
    );
    return () => clearTimeout(timer);
  }, [isSlideshow, index, assets, promo]);

  if (!promo || assets.length === 0) return null;

  const handleClick = async () => {
    if (opening) return;
    setOpening(true);

    // 別タブへ移るので、こちらの再生は止めておく。
    // 戻ってきたときに続きから見られるようにするため
    onPromoClick?.();

    // クリック直後に同期でタブを開く。await のあとに window.open すると
    // ユーザー操作起点とみなされずポップアップブロックされるため。
    let win = null;
    try {
      win = window.open('about:blank', '_blank');
      if (win) win.opener = null;
    } catch {
      win = null;
    }

    const url = await reportClick(promo.creative_id, placementSlug, keyword);

    if (url) {
      if (win) win.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    } else if (win) {
      // 記録に失敗したらタブを閉じる（空白タブを残さない）
      win.close();
    }
    setOpening(false);
  };

  const shown = isSlideshow ? index : 0;

  return (
    <button
      type="button"
      className={`promo-slot promo-slot--${variant} tr-${promo.slide_transition || 'none'}`}
      style={style}
      onClick={handleClick}
      title={promo.product_name}
    >
      <span className="promo-stage">
        {assets.map((asset, i) => (
          <img
            key={asset.url}
            src={asset.url}
            alt={asset.alt_text || promo.product_name}
            className={`promo-image${i === shown ? ' on' : ''}`}
            draggable={false}
          />
        ))}
      </span>
      {/* 広告であることを明示する（ステマ規制対応） */}
      <span className="promo-tag">PR</span>
    </button>
  );
};

export default PromoSlot;
